import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Checkbox,
  FormControlLabel,
  IconButton,
  MenuItem,
} from "@mui/material";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import TextFieldsOutlinedIcon from "@mui/icons-material/TextFieldsOutlined";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import AnalyticsOutlinedIcon from "@mui/icons-material/AnalyticsOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import TrainerMessengerPreview from "./TrainerMessengerPreview";
import ScenarioAnalytics from "../ScenarioAnalytics";
import {
  getGame,
  saveGameSettings,
  saveTrainerQuestions,
  loadTrainerVideo,
  loadPoster,
  getProfile,
} from "../../redux/asyncThunk/userActions";
import { sanitizePlainText } from "../../utils/inputValidation";
import {
  getProjectStatusFromSettings,
  settingsFromProjectStatus,
  mergeGameSettings,
} from "../../utils/projectAccess";
import ProjectMessengerAvailability, {
  readAvailableMessengersFromSettings,
} from "../ProjectMessengerAvailability";
import { AUTH_FIELD_SX } from "../../pages/AuthLayout";
import { userSlice } from "../../redux/user/userSlice";
import InputFileUpload from "../UploadFile";
import "./trainer-editor.css";

const { addMessage } = userSlice;
const fieldSx = { ...AUTH_FIELD_SX, maxWidth: "100%", width: "100%" };

function createEmptyQuestion() {
  const id = `q-${Date.now()}`;
  return {
    id,
    questionMode: "text",
    questionText: "",
    hasVideo: false,
    screenpath: "",
    answers: [
      { id: `${id}-a1`, label: "", isCorrect: false },
      { id: `${id}-a2`, label: "", isCorrect: false },
    ],
  };
}

export default function TrainerEditor({ game: initialGame }) {
  const dispatch = useDispatch();
  const game = useSelector((state) => state.user.game) ?? initialGame;
  const user = useSelector((state) => state.user?.data?.user);
  const profile = useSelector((state) => state.user?.profile);

  const [view, setView] = useState("editor");
  const [title, setTitle] = useState(game?.title ?? "");
  const [trainerTheme, setTrainerTheme] = useState(game?.trainerTheme ?? "");
  const [description, setDescription] = useState(game?.description ?? "");
  const [questions, setQuestions] = useState(game?.trainerQuestions ?? []);
  const [activeQuestionId, setActiveQuestionId] = useState(null);
  const [settings, setSettings] = useState({
    projectStatus: getProjectStatusFromSettings(game?.settings),
    completion_text: game?.settings?.completion_text ?? "",
    author_telegram: game?.settings?.author_telegram ?? "",
    allow_replay: game?.settings?.allow_replay !== false,
  });
  const [saving, setSaving] = useState(false);
  const [availableMessengers, setAvailableMessengers] = useState(
    readAvailableMessengersFromSettings(game?.settings),
  );

  const activeQuestion = useMemo(
    () => questions.find((q) => q.id === activeQuestionId) ?? null,
    [questions, activeQuestionId]
  );

  useEffect(() => {
    if (user?.id && !profile?.login) {
      dispatch(getProfile({ id: user.id }));
    }
  }, [dispatch, user?.id, profile?.login]);

  useEffect(() => {
    if (!game) return;
    setTitle(game.title ?? "");
    setTrainerTheme(game.trainerTheme ?? "");
    setDescription(game.description ?? "");
    setQuestions(game.trainerQuestions ?? []);
    setSettings({
      projectStatus: getProjectStatusFromSettings(game.settings),
      completion_text: game.settings?.completion_text ?? "",
      author_telegram: game.settings?.author_telegram ?? "",
      allow_replay: game.settings?.allow_replay !== false,
    });
    setAvailableMessengers(readAvailableMessengersFromSettings(game.settings));
  }, [game]);

  const updateActiveQuestion = (patch) => {
    if (!activeQuestionId) return;
    setQuestions((prev) =>
      prev.map((q) => (q.id === activeQuestionId ? { ...q, ...patch } : q))
    );
  };

  const updateAnswer = (answerId, patch) => {
    if (!activeQuestion) return;
    updateActiveQuestion({
      answers: activeQuestion.answers.map((a) =>
        a.id === answerId ? { ...a, ...patch } : a
      ),
    });
  };

  const addAnswer = () => {
    if (!activeQuestion || activeQuestion.answers.length >= 4) return;
    updateActiveQuestion({
      answers: [
        ...activeQuestion.answers,
        {
          id: `${activeQuestion.id}-a${activeQuestion.answers.length + 1}`,
          label: "",
          isCorrect: false,
        },
      ],
    });
  };

  const handleAddQuestion = () => {
    const next = createEmptyQuestion();
    setQuestions((prev) => [...prev, next]);
    setActiveQuestionId(next.id);
  };

  const handleDeleteQuestion = (questionId) => {
    setQuestions((prev) => prev.filter((q) => q.id !== questionId));
    if (activeQuestionId === questionId) {
      setActiveQuestionId(null);
    }
  };

  const handleSaveAll = async () => {
    if (!game?._id) return;
    setSaving(true);
    try {
      await dispatch(
        saveGameSettings({
          game: game._id,
          title,
          description,
          program: trainerTheme,
          category: "",
          trainerTheme,
          settings: mergeGameSettings(game?.settings, {
            ...settingsFromProjectStatus(settings.projectStatus, game?.settings),
            contentType: "trainer",
            completion_text: settings.completion_text,
            author_telegram: settings.author_telegram,
            allow_replay: settings.allow_replay,
            availableMessengers,
          }),
        })
      ).unwrap();

      await dispatch(
        saveTrainerQuestions({
          gameId: game._id,
          trainerTheme,
          questions,
        })
      ).unwrap();

      dispatch(
        addMessage({ type: "info", message: "Тренажёр сохранён" })
      );
      dispatch(getGame({ gameId: game._id }));
    } catch {
      dispatch(addMessage({ type: "error", message: "Не удалось сохранить" }));
    } finally {
      setSaving(false);
    }
  };

  const handleVideoUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file || !activeQuestion || !user?.id || !game?._id) return;

    dispatch(
      loadTrainerVideo({
        userId: user.id,
        gameId: game._id,
        questionId: activeQuestion.id,
        file,
      })
    ).then((result) => {
      if (result.payload?.trainerQuestions) {
        setQuestions(result.payload.trainerQuestions);
      }
    });
  };

  const handlePoster = (event, gameId) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = () => {
      dispatch(
        loadPoster({
          gameId,
          base64String: reader.result,
          onSucces: () => dispatch(getGame({ gameId })),
        })
      );
    };
  };

  if (view === "analytics") {
    return (
      <div className="trainer-editor trainer-editor--analytics">
        <div className="trainer-editor__toolbar trainer-editor__toolbar--tabs">
          <Button
            variant="outlined"
            size="small"
            className="app-editor__tab app-editor__tab--active"
            startIcon={<AnalyticsOutlinedIcon />}
            disabled
          >
            Аналитика
          </Button>
          <Button
            variant="outlined"
            size="small"
            className="app-editor__tab"
            startIcon={<EditOutlinedIcon />}
            onClick={() => setView("editor")}
          >
            Редактор
          </Button>
        </div>
        <ScenarioAnalytics embedded />
      </div>
    );
  }

  return (
    <div className="trainer-editor">
      <div className="trainer-editor__toolbar trainer-editor__toolbar--tabs">
        <Button
          variant="outlined"
          size="small"
          className="app-editor__tab app-editor__tab--active"
          startIcon={<EditOutlinedIcon />}
          disabled
        >
          Редактор
        </Button>
        <Button
          variant="outlined"
          size="small"
          className="app-editor__tab"
          startIcon={<AnalyticsOutlinedIcon />}
          onClick={() => setView("analytics")}
        >
          Аналитика
        </Button>
        <Button
          variant="outlined"
          size="small"
          className="app-editor__tab app-editor__tab"
          startIcon={<SaveOutlinedIcon />}
          disabled={saving}
          onClick={handleSaveAll}
        >
          {saving ? "Сохранение…" : "Сохранить"}
        </Button>
      </div>

      <div className="trainer-editor__layout">
        <div className="trainer-editor__form">
          <section className="trainer-editor__card">
            <h3>Настройки тренажёра</h3>
            <TextField
              label="Название"
              size="small"
              fullWidth
              sx={fieldSx}
              value={title}
              onChange={(e) =>
                setTitle(sanitizePlainText(e.target.value, 120))
              }
            />
            <TextField
              label="Тема"
              size="small"
              fullWidth
              sx={fieldSx}
              value={trainerTheme}
              onChange={(e) =>
                setTrainerTheme(sanitizePlainText(e.target.value, 200))
              }
              placeholder="Например: вопросы по математике"
            />
            <TextField
              label="Приветственный текст"
              size="small"
              fullWidth
              multiline
              minRows={3}
              sx={fieldSx}
              value={description}
              onChange={(e) =>
                setDescription(sanitizePlainText(e.target.value, 2000))
              }
            />
            <TextField
              select
              label="Статус"
              size="small"
              sx={{ ...fieldSx, maxWidth: 220 }}
              value={settings.projectStatus}
              onChange={(e) =>
                setSettings((prev) => ({
                  ...prev,
                  projectStatus: e.target.value,
                }))
              }
            >
              <MenuItem value="draft">Черновик</MenuItem>
              <MenuItem value="published">Опубликован</MenuItem>
              <MenuItem value="hidden">Скрыт</MenuItem>
            </TextField>
            <TextField
              label="Telegram HR / автора"
              size="small"
              sx={{ ...fieldSx, maxWidth: 320 }}
              value={settings.author_telegram}
              onChange={(e) =>
                setSettings((prev) => ({
                  ...prev,
                  author_telegram: sanitizePlainText(e.target.value, 80),
                }))
              }
            />
            <div className="trainer-editor__poster-row">
              <InputFileUpload
                handleAddPoster={(event) => handlePoster(event, game._id)}
              />
              <span className="trainer-editor__hint">
                Обложка для стартового поста
              </span>
            </div>
            <ProjectMessengerAvailability
              gameId={game._id}
              value={availableMessengers}
              onChange={setAvailableMessengers}
              compact
              showShareLinks
            />
          </section>

          <section className="trainer-editor__card">
            <div className="trainer-editor__card-head">
              <h3>Вопросы темы</h3>
              <Button size="small" startIcon={<AddIcon />} onClick={handleAddQuestion}>
                Добавить
              </Button>
            </div>

            {!questions.length ? (
              <p className="trainer-editor__empty">
                Добавьте первый вопрос — сотрудники будут получать их случайным
                образом после «Старт».
              </p>
            ) : (
              <ul className="trainer-editor__question-list">
                {questions.map((q, index) => (
                  <li
                    key={q.id}
                    className={`trainer-editor__question-item${
                      activeQuestionId === q.id
                        ? " trainer-editor__question-item--active"
                        : ""
                    }`}
                  >
                    <button
                      type="button"
                      className="trainer-editor__question-btn"
                      onClick={() => setActiveQuestionId(q.id)}
                    >
                      {index + 1}.{" "}
                      {q.questionText?.trim() || "Без текста"}
                    </button>
                    <IconButton
                      size="small"
                      aria-label="Удалить вопрос"
                      onClick={() => handleDeleteQuestion(q.id)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </li>
                ))}
              </ul>
            )}

            {activeQuestion ? (
              <div className="trainer-editor__question-form">
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={activeQuestion.questionMode}
                  onChange={(_, value) => {
                    if (value) updateActiveQuestion({ questionMode: value });
                  }}
                >
                  <ToggleButton value="video">
                    <VideocamOutlinedIcon fontSize="small" />
                  </ToggleButton>
                  <ToggleButton value="text">
                    <TextFieldsOutlinedIcon fontSize="small" />
                  </ToggleButton>
                </ToggleButtonGroup>

                <TextField
                  label="Текст вопроса"
                  size="small"
                  fullWidth
                  multiline
                  minRows={2}
                  sx={fieldSx}
                  value={activeQuestion.questionText}
                  onChange={(e) =>
                    updateActiveQuestion({
                      questionText: sanitizePlainText(e.target.value, 2000),
                    })
                  }
                />

                {activeQuestion.questionMode === "video" ? (
                  <div>
                    <Button variant="outlined" component="label" size="small">
                      Загрузить видео
                      <input
                        hidden
                        type="file"
                        accept="video/*"
                        onChange={handleVideoUpload}
                      />
                    </Button>
                  </div>
                ) : null}

                <div className="trainer-editor__answers">
                  <span className="trainer-editor__answers-label">
                    Варианты ответа
                  </span>
                  {activeQuestion.answers.map((answer) => (
                    <div key={answer.id} className="trainer-editor__answer-row">
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="Текст ответа"
                        value={answer.label}
                        onChange={(e) =>
                          updateAnswer(answer.id, {
                            label: sanitizePlainText(e.target.value, 100),
                          })
                        }
                        sx={fieldSx}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={Boolean(answer.isCorrect)}
                            onChange={(e) =>
                              updateAnswer(answer.id, {
                                isCorrect: e.target.checked,
                              })
                            }
                          />
                        }
                        label="Верно"
                      />
                    </div>
                  ))}
                  {activeQuestion.answers.length < 4 ? (
                    <Button size="small" onClick={addAnswer}>
                      + Ответ
                    </Button>
                  ) : null}
                </div>
              </div>
            ) : null}
          </section>
        </div>

        <TrainerMessengerPreview
          title={title}
          theme={trainerTheme}
          description={description}
          poster={game?.poster}
          previewMode={activeQuestion ? "question" : "welcome"}
          question={activeQuestion}
        />
      </div>
    </div>
  );
}
